import { useEffect } from 'react';
import * as Sentry from '@sentry/react';
import { useSearchParams } from 'react-router-dom';
import { ScreenShotGenerationParamName } from '@sparrow-card/sparrow-card-constants';
import { CityInput } from 'components/Input/templates/City';
import { StateInput } from 'components/Input/templates/State';
import { StreetInput } from 'components/Input/templates/Street';
import { UnitInput } from 'components/Input/templates/Unit';
import { ZipInput } from 'components/Input/templates/Zip';
import { type TooltipMessage } from 'enums/Messages';
import useScript, { ScriptStatus } from 'hooks/useScript';
import { type FCC } from 'types';
import { GOOGLE_API } from './config';

export interface InputName {
  street: string;
  city: string;
  state: string;
  unit: string;
  zip: string;
}

export type ClassNames = Partial<Record<keyof InputName, string>>;

interface Props {
  names: InputName;
  labels: InputName;
  classNames?: ClassNames;
  requiredFields: Record<keyof InputName, boolean>;
  streetTooltip: TooltipMessage;
}

const LocationInput: FCC<Props> = ({
  names,
  labels,
  classNames,
  requiredFields,
  streetTooltip,
}) => {
  const status = useScript(GOOGLE_API);
  const isLoaded = status === ScriptStatus.Ready;
  const isError = status === ScriptStatus.Error;
  const [searchParams] = useSearchParams();
  const isNotScreenshot = searchParams.get(ScreenShotGenerationParamName) !== 'true';

  useEffect(() => {
    if (isError && isNotScreenshot) {
      Sentry.captureMessage('Failed to fetch Google API');
    }
  }, [isError, isNotScreenshot]);

  return (
    <>
      <StreetInput
        names={names}
        label={labels.street}
        googlePlacesScriptLoaded={isLoaded}
        containerClassName={classNames?.street}
        required={requiredFields.street}
        streetTooltip={streetTooltip}
      />
      <UnitInput
        name={names.unit}
        label={labels.unit}
        containerClassName={classNames?.unit}
        required={requiredFields.unit}
      />
      <CityInput
        name={names.city}
        label={labels.city}
        containerClassName={classNames?.city}
        required={requiredFields.city}
      />
      <StateInput
        name={names.state}
        label={labels.state}
        containerClassName={classNames?.state}
        required={requiredFields.state}
      />
      <ZipInput
        name={names.zip}
        label={labels.zip}
        containerClassName={classNames?.zip}
        required={requiredFields.zip}
      />
    </>
  );
};

export default LocationInput;
